import { AfterViewInit, Component, OnInit, OnDestroy } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Subscription, of, catchError } from 'rxjs';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { Logger, PreviewMode, UtilityService } from 'src/app/@shared';
import { OfferPromo, PromoDomain, OfferPromoService, OfferService, Offer, OfferDomain, OfferGroupDomain } from '../..';
import { SelectionModel } from '@angular/cdk/collections';
import { Status } from '../../models/offer-promo-status';
import { AuthorizeControlService } from 'pr1-ui-components';
import { ActivatedRoute, Router } from '@angular/router';
import { EventWorkflowService } from '../../services/event-workflow.service';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';

const log = new Logger('VersionPromoTableComponent');

@Component({
  selector: 'app-version-promo-table',
  templateUrl: './version-promo-table.component.html',
  styleUrls: ['./version-promo-table.component.scss'],
})
export class VersionPromoTableComponent<TOfferPromo extends OfferPromo, TPromoDomain extends PromoDomain<OfferPromo>, T extends Offer, TOfferDomain extends OfferDomain<Offer>> implements OnInit, AfterViewInit, OnDestroy {
  Status: typeof Status = Status;
  PreviewMode = PreviewMode;
  viewModel$ = combineLatest([
    this.offerpromoService.getpromoChildVersions$,
    this.offerpromoService.isLoading$
  ]).pipe(
    map(([promos, isLoading]) => {
      this.promoVariantList = new MatTableDataSource(promos);
      return { promos, isLoading };
    }),
    catchError(error => {
      log.error('Error loading view model', error);
      return of({ promos: [], isLoading: false });
    })
  );

  promoVariantList: MatTableDataSource<OfferGroupDomain<Offer>> = new MatTableDataSource();
  selection = new SelectionModel<OfferGroupDomain<Offer>>(true, [], true, (o1, o2) => o1.DomainId === o2.DomainId);
  readOnlyMode = true;
  private subscriptions: Subscription[] = [];
  displayedColumns = ['select', 'Image', 'Name', 'Version', 'Headline', 'BodyCopy', 'EventType', 'VersionCodes', 'Status', 'Rank', 'Actions'];

  constructor(
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    public utilityService: UtilityService,
    private mediaObserver: MediaObserver,
    private authorizationService: AuthorizeControlService,
    public offerService: OfferService<T, TOfferDomain>,
    private router: Router,
    private route: ActivatedRoute,
    private ewService: EventWorkflowService
  ) { }

  ngOnInit(): void {
    log.debug('init');
    this.detectViewportSizeChange();

    this.readOnlyMode = this.offerpromoService.getOfferDetailsReadOnlyValue();
    this.subscriptions.push(this.offerpromoService.offerDetailsReadOnlyMode$.subscribe((res) => {
      this.readOnlyMode = res;
    }));
  }

  ngAfterViewInit() {
    if (this.ewService.readOnlyMode != null) {
      this.readOnlyMode = this.ewService.readOnlyMode;
      return;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  detectViewportSizeChange() {
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.subscriptions.push(this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        )
      ).subscribe((change) => {
        if (change.some((x) => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'Name', 'Actions'];
        } else if (change.some((x) => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'Name', 'Headline', 'Actions'];
        } else {
          this.displayedColumns = ['select', 'Image', 'Name', 'Version', 'Headline', 'BodyCopy', 'EventType', 'VersionCodes', 'Status', 'Rank', 'Actions'];
        }
      }));
  }

  onImageError(event: Event): void {
    const element = event.target as HTMLImageElement;
    element.src = './assets/images/default-image.png'; // Path to your default image
  }

  onEdit(id: string) {
    this.router.navigate([`../../../${id}/promos/promodetails`], { relativeTo: this.route });
  }

  onSort(sortState: Sort): void {
    this.offerpromoService.sort(sortState);
  }

  clearSelection() {
    this.selection.clear();
  }

  updateStatus(status: Status, row: OfferGroupDomain<Offer>) {
    let selectedRecordsDetails: OfferGroupDomain<Offer>[] = [JSON.parse(JSON.stringify(row))];
    this.updateRecordsStatus(selectedRecordsDetails, status);
  }

  updateRecordsStatus(selectedRecordsDetails: Array<any>, status: Status) {
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      selectedRecordsDetails = selectedRecordsDetails.map((x: any) => {
        let obj ={
          Id: x.Detail.Id,
          Label: x.EventOfferType,
          Status: status as unknown as string
        }
        return obj;
      });
      this.offerpromoService.updateStatus(selectedRecordsDetails).subscribe({
        next: () => {
          this.offerpromoService.reloadPromoVersions();
          this.clearSelection();
          this.offerpromoService.updateRecordStatus(new Date());
        },
        error: (err) => {
          log.error('Failed to update status', err);
        }
      });
    }
  }

  getSelectedSectionRecords() {
    return this.selection.selected;
  }

  isAllSelected(): boolean {
    return this.promoVariantList.data.every(promo => this.selection.isSelected(promo));
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.promoVariantList.data.forEach(promo => {
        if (!this.selection.isSelected(promo)) {
          this.selection.select(promo);
        }
      });
    } else {
      this.promoVariantList.data.forEach(promo => {
        this.selection.deselect(promo);
      });
    }
  }

  isSomeSelectedOnPage(): boolean {
    return this.promoVariantList.data.some(promo => this.selection.isSelected(promo));
  }

  toggleSelect(checked: boolean, promo: OfferGroupDomain<Offer>) {
    if (checked) {
      if (!this.selection.isSelected(promo)) {
        this.selection.select(promo);
      }
    } else {
      this.selection.deselect(promo);
    }
  }

  canEdit(): boolean {
    return this.authorizationService.checkAccess('edit||offermang') && !this.readOnlyMode;
  }

  getStatusClass(promo: OfferGroupDomain<Offer>): string {
    const status = promo?.Detail?.Status;
    return status === '0' ? 'statusPENDING' : 'status' + status;
  }

  getStatusText(promo: OfferGroupDomain<Offer>): string {
    const status = promo?.Detail?.Status;
    return status === '0' ? 'PENDING' : status || '';
  }
}
